import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Store, 
  Utensils, 
  BriefcaseBusiness,
  Film,
  Cookie,
  Calendar, 
  MessageSquare, 
  BookOpen,
  Users,
  UserCheck,
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

const sidebarItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", roles: ["admin", "manager", "operator"] },
  { icon: Store, label: "Stores", path: "/stores", roles: ["admin", "manager", "operator"] },
  { icon: Utensils, label: "Restaurants", path: "/restaurants", roles: ["admin", "manager", "operator"] },
  { icon: BriefcaseBusiness, label: "Services", path: "/services", roles: ["admin", "manager", "operator"] },
  { icon: Film, label: "Movies", path: "/movies", roles: ["admin", "manager", "operator"] },
  { icon: Cookie, label: "Snacks", path: "/snacks", roles: ["admin", "manager", "operator"] },
  { icon: Calendar, label: "Bookings", path: "/bookings", roles: ["admin", "manager", "operator"] },
  { icon: MessageSquare, label: "Conversations", path: "/conversations", roles: ["admin", "manager", "operator"] },
  { icon: BookOpen, label: "Knowledge Base", path: "/knowledge", roles: ["admin", "manager", "operator"] },
  { icon: UserCheck, label: "Registered Users", path: "/registered-users", roles: ["admin", "manager"] },
  { icon: Users, label: "CMS Users", path: "/users", roles: ["admin"] },
  { icon: Settings, label: "Settings", path: "/settings", roles: ["admin", "manager", "operator"] },
];
export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const filteredItems = sidebarItems.filter(item => 
    item.roles.includes(user?.role || "operator")
  );

  const currentPage = sidebarItems.find(item => item.path === location.pathname);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 z-40 h-screen transition-all duration-300 sidebar",
          sidebarOpen ? "w-64" : "w-20"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-white/10">
            {sidebarOpen && (
              <div className="flex items-center gap-2 animate-fade-in">
                <div className="w-8 h-8 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="text-white font-semibold text-lg tracking-tight font-['Manrope']">MallChat</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-white/10"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
            {filteredItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 text-sm font-medium sidebar-item",
                    isActive 
                      ? "active text-white" 
                      : "text-white/70 hover:text-white"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="animate-fade-in">{item.label}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-none bg-gradient-to-r from-[#EC1A23] to-[#561D1A] flex items-center justify-center text-white font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0 animate-fade-in">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-white/60 capitalize">{user?.role}</p>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "flex-1 transition-all duration-300",
        sidebarOpen ? "ml-64" : "ml-20"
      )}>
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 glass-header border-b border-border">
          <div className="flex h-full items-center justify-between px-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Dashboard</span>
              {currentPage && currentPage.path !== "/dashboard" && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="text-foreground font-medium">{currentPage.label}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
